import {
  newTracker,
  trackPageView,
  flushBuffer,
  enableActivityTracking,
  trackStructEvent,
} from '@snowplow/browser-tracker';
import * as Sentry from '@sentry/browser';
import { FormTrackingPlugin, enableFormTracking } from '@snowplow/browser-plugin-form-tracking';
import {
  LinkClickTrackingPlugin,
  enableLinkClickTracking,
} from '@snowplow/browser-plugin-link-click-tracking';
import { formatGraphQLErrorMessage } from '~/javascripts/lib/error_utils';
import trackEventMutation from '~/javascripts/tracking/graphql/mutations/event_tracking.mutation.graphql';
import getStandardContext from './standard_context';

function dispatcher(data) {
  return trackStructEvent(data);
}

function dispatcherNull() {
  return false;
}

function hasTracker() {
  return typeof window.snowplowOptions === 'object';
}

export function initTracking() {
  if (!hasTracker()) {
    return;
  }

  const { namespace, endpoint, ...options } = window.snowplowOptions;
  newTracker(namespace, endpoint, {
    ...options,
    plugins: [FormTrackingPlugin(), LinkClickTrackingPlugin()],
  });

  const standardContext = getStandardContext();
  flushBuffer();
  enableFormTracking();
  enableLinkClickTracking({
    pseudoClicks: true,
    context: [standardContext],
  });
  enableActivityTracking({
    heartbeatDelay: 30,
    minimumVisitLength: 30,
  });
  trackPageView({ context: [standardContext] });
}

export default class Tracking {
  constructor(shouldTrack = true) {
    this.dispatchEvent = hasTracker() && shouldTrack ? dispatcher : dispatcherNull;
  }

  event(category = document.body.dataset.page, action = 'generic', data = {}) {
    Tracking.validateCategory(category);

    const { label, property, value, contexts = [] } = data;

    Tracking.validateTrackingValue(value);

    return this.dispatchEvent({
      category,
      action,
      label,
      property,
      value,
      context: contexts.length ? contexts : [getStandardContext()],
    });
  }

  // eslint-disable-next-line max-params
  static async trackEventOnBackend(apolloClient, category, action, options = {}) {
    if (!apolloClient) {
      throw new Error('Tracking: ApolloClient not provided to initiate backend tracking.');
    }

    Tracking.validateCategory(category);

    const { label, property, value, contexts = [] } = options;

    Tracking.validateTrackingValue(value);

    try {
      const { data } = await apolloClient.mutate({
        mutation: trackEventMutation,
        variables: {
          eventName: action,
          eventCategory: category,
          eventLabel: label,
          eventProperty: property,
          eventValue: value,
          contextData: contexts.length ? contexts : [getStandardContext()],
        },
      });

      if (data?.trackEvent?.errors?.length > 0) {
        throw formatGraphQLErrorMessage(data.trackEvent.errors);
      }
    } catch (error) {
      Sentry.captureException(error, {
        category,
        action,
        data: options,
        message: 'Failed to track event on backend',
      });
    }
  }

  static validateCategory(category) {
    if (!category) {
      throw new Error('Tracking: no category provided for tracking.');
    }
  }

  static validateTrackingValue(value) {
    if (value && !Number.isInteger(parseInt(value, 10))) {
      throw new Error(
        `Tracking: value must be a number or a string that can be converted to one. Received ${typeof value} for ${value} instead`,
      );
    }
  }
}
